import {Table, Tag} from 'antd';
import {useOpenSupportTicket} from '../useOpenSupportTicket';
import styles from './TicketsTable.module.scss';
import {shortenTicketId} from '../utils/shortenTicketId';
import {TicketUserAvatar} from './TicketUserAvatar';
import {AwaitingReplyBadge} from './AwaitingReplyBadge';
import {CsatStars, csatTier} from './CsatStars';
import {TicketRowActions} from './TicketRowActions';
import UserLink from 'components/userLink/UserLink';

const STATUS_COLORS = {
  GUIDED_FLOW: 'blue',
  AI_ACTIVE: 'purple',
  HUMAN_ACTIVE: 'orange',
  ESCALATED: 'red',
  RESOLVED: 'green',
  ABANDONED: 'default',
};

export const TicketsTable = ({
  tickets,
  loading,
  currentPage,
  pageSize,
  total,
  onPageChange,
  onChanged,
  showUser = true,
  showCreatedAt = false,
}) => {
  const openTicket = useOpenSupportTicket();

  const getSentimentDot = sentiment => {
    switch (sentiment) {
      case 'positive':
        return <span className={`${styles.dot} ${styles.green}`}></span>;
      case 'negative':
        return <span className={`${styles.dot} ${styles.red}`}></span>;
      default:
        return <span className={`${styles.dot} ${styles.amber}`}></span>;
    }
  };

  const columns = [
    {
      title: 'Ticket ID',
      dataIndex: 'id',
      key: 'id',
      width: 110,
      render: text => (
        <span className={styles.modelTag} title={text}>
          {shortenTicketId(text)}
        </span>
      ),
    },
    {
      title: 'User',
      dataIndex: 'userFirstName',
      key: 'user',
      render: (text, record) => (
        <div className={styles.cellUser}>
          {getSentimentDot(record.sentiment)}
          <TicketUserAvatar
            imageUrl={record.userImageUrl}
            initials={record.userInitials}
            size="md"
          />
          <div className={styles.userDetails}>
            {record.userId ? (
              <UserLink
                userId={record.userId}
                tab="overview"
                className={styles.nameLink}>
                {record.userDisplayName}
              </UserLink>
            ) : (
              <span
                className={styles.nameLink}
                title="Anonymous ticket — no account linked">
                {record.userDisplayName}
              </span>
            )}
            <span className={styles.subline}>{record.userSecondaryText}</span>
            <AwaitingReplyBadge awaiting={record.awaitingReply} />
          </div>
        </div>
      ),
    },
    {
      title: 'Country',
      dataIndex: 'userCountry',
      key: 'userCountry',
      width: 100,
      render: text => <span className={styles.cellMeta}>{text || '—'}</span>,
    },
    {
      title: 'Language',
      dataIndex: 'userLanguage',
      key: 'userLanguage',
      width: 110,
      render: text => <span className={styles.cellMeta}>{text || '—'}</span>,
    },
    {
      title: 'Issue',
      dataIndex: 'intentCategory',
      key: 'intentCategory',
      render: text => <span className={styles.intentText}>{text || '—'}</span>,
    },
    {
      title: 'Latest Message',
      dataIndex: 'lastMessage',
      key: 'lastMessage',
      render: (text, record) =>
        text ? (
          <span className={styles.lastMessageBubble} title={text}>
            {record.lastMessageSender ? (
              <span className={styles.lastMessageSender}>
                {record.lastMessageSender}
              </span>
            ) : null}
            <span className={styles.lastMessageText}>{text}</span>
          </span>
        ) : (
          <span className={styles.cellMeta}>—</span>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (text, record) => (
        <span className={styles.statusCell}>
          <Tag color={STATUS_COLORS[text] || 'default'}>
            {text?.replace('_', ' ')}
          </Tag>
          {record.isAnonymous ? (
            <Tag
              color="gold"
              title={
                record.userId
                  ? 'Originally anonymous — linked to an account'
                  : 'Anonymous — no account linked'
              }>
              {record.userId ? 'Anon · Linked' : 'Anonymous'}
            </Tag>
          ) : null}
        </span>
      ),
    },
    {
      title: 'CSAT',
      dataIndex: 'csatScore',
      key: 'csatScore',
      width: 130,
      render: (score, record) => {
        // Show "Unrated" only when the ticket is closed but the customer
        // never rated. For not-yet-resolvable states an em-dash is calmer.
        const isClosed =
          record.status === 'RESOLVED' || record.status === 'ABANDONED';
        if (score === null || score === undefined) {
          return (
            <span className={styles.cellMeta}>
              {isClosed ? 'Unrated' : '—'}
            </span>
          );
        }
        return <CsatStars score={score} size="sm" />;
      },
    },
    showCreatedAt
      ? {
          title: 'Created',
          dataIndex: 'createdAt',
          key: 'createdAt',
          render: value => {
            const date = value ? new Date(value) : null;
            if (!date || Number.isNaN(date.getTime())) return '—';
            return (
              <time dateTime={date.toISOString()} className={styles.createdAt}>
                <span>
                  {date.toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </span>
                <span className={styles.cellMeta}>
                  {date.toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short',
                  })}
                </span>
              </time>
            );
          },
        }
      : {
          title: 'Time Elapsed',
          dataIndex: 'timeElapsed',
          key: 'timeElapsed',
          render: text => <span className={styles.cellTime}>{text}</span>,
        },
    {
      title: '',
      key: 'action',
      width: 60,
      render: (_, record) => (
        <TicketRowActions
          ticket={record}
          onChanged={onChanged}
          actionBtnClassName={styles.actionBtn}
          threeDotClassName={styles.threeDot}
        />
      ),
    },
  ];

  return (
    <div className={styles.tableWrapper}>
      <Table
        className="antd-table"
        columns={columns.filter(column => showUser || column.key !== 'user')}
        dataSource={tickets}
        rowKey="id"
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          pageSizeOptions: ['12', '24', '48'],
          onChange: onPageChange,
          onShowSizeChange: onPageChange,
        }}
        onRow={record => {
          const tier = csatTier(record.csatScore);
          const tierClass =
            tier === 'low'
              ? styles.lowCsatRow
              : tier === 'high'
              ? styles.highCsatRow
              : '';
          return {
            onClick: () => openTicket(record.id),
            className: `${styles.clickableRow} ${tierClass}`.trim(),
          };
        }}
      />
    </div>
  );
};
